import client from 'utils/request'

/**
 * Presence heartbeat + reliable offline signal.
 *
 * While logged in and the tab is visible, ping user-management every 30s so the
 * Admin Dashboard reflects users actually still active. On logout AND on
 * tab-close we send an immediate "offline" beacon so the user drops off right
 * away instead of lingering until the heartbeat window expires.
 *
 * - The JWT is read from the in-memory axios default header (still set even
 *   after localStorage is cleared on logout) — no id is ever sent by the client.
 * - Hidden/background tabs do not ping, so an idle-open tab is not counted.
 * - keepalive lets the offline beacon survive page unload (tab close).
 * - If the beacon never arrives (e.g. crash), the 90s server window is the backstop.
 */
const HEARTBEAT_INTERVAL_MS = 30 * 1000

let intervalId = null

const ping = () => {
  if (document.visibilityState !== 'visible') return
  client.post('/users/heartbeat').catch(() => {})
}

// Fire-and-forget "I'm leaving" — used on explicit logout and on tab-close.
const sendOffline = () => {
  try {
    const base = (client.defaults.baseURL || '').replace(/\/$/, '')
    const token =
      (client.defaults.headers &&
        client.defaults.headers.common &&
        client.defaults.headers.common.Authorization) ||
      localStorage.getItem('token')
    if (!base || !token) return
    fetch(`${base}/users/offline`, {
      method: 'POST',
      headers: { Authorization: token },
      keepalive: true,
    }).catch(() => {})
  } catch (e) {
    /* best-effort */
  }
}

const handleVisibility = () => {
  if (document.visibilityState === 'visible') ping()
}

const handlePageHide = () => sendOffline()

export const startPresenceHeartbeat = () => {
  if (intervalId) return // already running
  ping() // stamp immediately
  intervalId = setInterval(ping, HEARTBEAT_INTERVAL_MS)
  document.addEventListener('visibilitychange', handleVisibility)
  window.addEventListener('pagehide', handlePageHide)
}

export const stopPresenceHeartbeat = () => {
  if (intervalId) {
    clearInterval(intervalId)
    intervalId = null
  }
  document.removeEventListener('visibilitychange', handleVisibility)
  window.removeEventListener('pagehide', handlePageHide)
  sendOffline() // explicit logout → go offline immediately
}

// Kept for callers that want an explicit offline (e.g. admin signOut).
export const markOffline = () => {
  sendOffline()
  return Promise.resolve()
}

export default startPresenceHeartbeat
